import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Bookmark, Download, Palette, Mic } from "lucide-react";
import { getSaved, useLibraryVersion } from "@/lib/library";
import { NavSearch } from "@/components/site/NavSearch";

const LINKS = [
  { label: "The Network", href: "#networks" },
  { label: "Manifesto", href: "#manifesto" },
  // { label: "Team", href: "#team" }, // TEMP HIDDEN — restore to bring back Team nav link
  { label: "Stories", href: "#stories" },
  // { label: "Shop", href: "#store" }, // TEMP HIDDEN — restore to bring back Shop nav link
  { label: "Get Featured", href: "#featured" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  useLibraryVersion();
  const savedCount = getSaved().length;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = LINKS.map((l) => l.href.slice(1));
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-[1000] transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
        <a href="#top" data-testid="nav-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
          <span className="text-[#E30613]">VL</span>
          <span className="text-white">MAG</span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => {
            const id = l.href.slice(1);
            const isActive = active === id;
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${id}`}
                className={`relative text-xs uppercase tracking-[0.22em] font-bold transition-colors duration-300 ${isActive ? "text-white" : "text-neutral-400 hover:text-white"}`}
              >
                {l.label}
                <span className={`absolute -bottom-1.5 left-0 h-[2px] bg-[#E30613] transition-all duration-300 ${isActive ? "w-full" : "w-0"}`} />
              </a>
            );
          })}
          <span className="h-4 w-px bg-white/15" aria-hidden />
          <Link
            to="/podcast"
            data-testid="nav-podcast"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-neutral-400 hover:text-white transition-colors duration-300"
          >
            <Mic size={13} /> Podcast
          </Link>
          <Link
            to="/art"
            data-testid="nav-art"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-neutral-400 hover:text-white transition-colors duration-300"
          >
            <Palette size={13} /> Art
          </Link>
          <Link
            to="/radio"
            data-testid="nav-radio"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-white hover:text-[#E30613] transition-colors duration-300"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#E30613]" />
            </span>
            Radio
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <span className="h-5 w-px bg-white/20" aria-hidden />
          <a
            href="#featured"
            data-testid="nav-cta"
            className="relative overflow-hidden border border-[#E30613] px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-bold group"
          >
            <span className="relative z-10 text-white group-hover:text-white transition-colors">Submit Your Work</span>
            <span className="absolute inset-0 bg-[#E30613] -translate-x-full group-hover:translate-x-0 transition-transform duration-400 ease-out" />
          </a>

          <NavSearch />

          <Link
            to="/saved"
            data-testid="nav-saved"
            aria-label="Saved"
            className="relative inline-flex items-center justify-center w-11 h-11 border border-white/20 text-neutral-300 hover:text-white hover:border-white/60 transition-colors"
          >
            <Bookmark size={17} />
            {savedCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#E30613] text-white text-[10px] font-bold rounded-full" data-testid="nav-saved-count">{savedCount}</span>
            )}
          </Link>
        </div>

        <button
          className="md:hidden text-white"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black border-t border-white/10"
          >
            <div className="flex flex-col px-5 py-4">
              <NavSearch mobile onDone={() => setOpen(false)} />
              <p className="pb-1 text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-600">Browse</p>
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.href.slice(1)}`}
                  className="py-4 border-b border-white/10 text-lg uppercase tracking-wide font-bold text-neutral-300"
                >
                  {l.label}
                </a>
              ))}
              <p className="pt-5 pb-1 text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-600">Discover</p>
              <Link
                to="/podcast"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-podcast"
                className="py-4 border-b border-white/10 flex items-center gap-3 text-lg uppercase tracking-wide font-bold text-neutral-300"
              >
                <Mic size={18} className="text-[#E30613]" /> Podcast
              </Link>
              <Link
                to="/art"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-art"
                className="py-4 border-b border-white/10 flex items-center gap-3 text-lg uppercase tracking-wide font-bold text-neutral-300"
              >
                <Palette size={18} className="text-[#E30613]" /> Art
              </Link>
              <Link
                to="/radio"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-radio"
                className="py-4 border-b border-white/10 flex items-center gap-3 text-lg uppercase tracking-wide font-bold text-white"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E30613]" />
                </span>
                Radio
              </Link>
              <Link
                to="/saved"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-saved"
                className="py-4 flex items-center gap-3 text-lg uppercase tracking-wide font-bold text-neutral-300"
              >
                <Bookmark size={18} className="text-[#E30613]" /> Saved{savedCount > 0 ? ` (${savedCount})` : ""}
              </Link>
              <button
                onClick={() => { setOpen(false); window.dispatchEvent(new Event("vlmag-install-open")); }}
                data-testid="nav-mobile-install"
                className="py-4 mt-2 w-full inline-flex items-center justify-center gap-2 border border-[#E30613] text-white text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#E30613] transition-colors"
              >
                <Download size={16} /> Install App
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
